import { useEffect, useMemo, useRef, useState } from "react";
import { useApiOperation } from "src/api/hooks";
import { deviceControlExcuteAsync, getBuildingDeviceControlAsync } from "src/api/iot/iot-api";
import BaseRichTooltip from "src/components/BaseRichTooltip";
import useIntersectionObserver from "src/hooks/useIntersectionObserver";
import { BULLET, roundFloat } from "src/utils/common-util";
import { isCaution, isPassExecute } from "../monitoring-func";
import { requestWithRetry } from "../monitoring-request";
import { DeviceControlType, ExecuteResultType } from "../monitoring-types";

type Props = {
  devices: ExecuteResultType[];
};

function isValidJSONString(value: string) {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

const IotTemperatureAverage = ({ devices }: Props) => {
  const target = useRef(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [localDevices, setLocalDevices] = useState<ExecuteResultType[]>(devices);

  const { executeAsync: getIotControl } = useApiOperation(getBuildingDeviceControlAsync, { noErrorModal: true, noLoading: true });
  const { executeAsync: executeDevice } = useApiOperation(deviceControlExcuteAsync, { noErrorModal: true, noLoading: true });

  // 기존 요청 취소 함수
  const abortPendingRequests = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const startNewRequests = () => {
    abortPendingRequests();
    abortControllerRef.current = new AbortController();

    return abortControllerRef.current.signal;
  };

  const { observe } = useIntersectionObserver(
    async () => {
      const signal = startNewRequests();
      const updatedResults = await Promise.all(localDevices.map((localDevice) => getControlDevicesAndExecute(localDevice, signal)));
      // undefined나 null 값을 제외하고 원래 항목과 병합
      const validUpdated = updatedResults.map((result, index) => {
        if (!result) return { ...localDevices[index], isLoading: false }; // 실패하면 원래 값을 두되 로딩은 끈다
        return { ...localDevices[index], ...result };
      });
      setLocalDevices(validUpdated);
    },
    {
      threshold: 0.5,
    },
  );

  const executeControlDevice = async (controlDevices: DeviceControlType[], signal: AbortSignal) => {
    const execute = async (item: DeviceControlType) => {
      if (!isPassExecute(item)) return;

      const outcome = await requestWithRetry(() =>
        executeDevice({
          deviceType: "building",
          deviceId: item.buildingDeviceId,
          controlId: item.control.id,
          controlMethod: item.control.method,
          arguments: "",
          cacheable: true,
          signal,
        }),
      );

      // 취소는 화면에서 벗어났거나 새 조회가 시작된 것이라 그대로 둔다
      if (outcome.status === "canceled") return;
      if (outcome.status === "failed") {
        console.error(`최종 실패: deviceId ${item.buildingDeviceId} - ${outcome.error}`);
        return;
      }

      const { data } = outcome.value;
      const parsedResult = isValidJSONString(data.data.result) ? JSON.parse(data.data.result) : data.data.result;

      return { ...item, result: parsedResult, isLoading: false };
    };

    try {
      const allPromiseResults = await Promise.all(controlDevices.map(execute));

      const validResults = allPromiseResults.filter(Boolean); // undefined, null 모두 제거
      return Object.assign({}, ...validResults);
    } catch (error) {
      console.error("executeControlDevice 전체 실패:", error);
    }
  };

  // 제어 목록 조회 api
  const getControlDevicesAndExecute = async (controlDevice: ExecuteResultType, signal: AbortSignal) => {
    const { buildingDeviceId, labelType, location, platform, internalDisplayName, isLoading } = controlDevice;

    const outcome = await requestWithRetry(() => getIotControl({ id: buildingDeviceId, deviceType: "building", signal }));

    if (outcome.status === "canceled") return;
    if (outcome.status === "failed") {
      console.error(`controls 실행 최종 실패: deviceId ${buildingDeviceId} - ${outcome.error}`);
      return;
    }

    try {
      const result = outcome.value.data.data.controls.map((control) => ({
        ...control,
        labelType,
        location,
        platform,
        internalDisplayName,
        isLoading,
      }));

      const executeList = result
        .filter((result) => result !== undefined)
        .flat()
        .filter((item) => item?.control.method === "READ_STATUS");

      return await executeControlDevice(executeList as DeviceControlType[], signal);
    } catch (error) {
      // 200 인데 응답 형태가 예상과 다른 경우. 한 기기 때문에 배치 전체가 멈추면 안 된다
      console.error(`controls 응답 처리 실패: deviceId ${buildingDeviceId} - ${error}`);
    }
  };

  useEffect(() => {
    observe(target.current);

    return () => {
      abortPendingRequests();
    };
  }, []);

  // 온도 정보 처리 - useMemo로 최적화
  const { temperatureList, formattedAverage } = useMemo(() => {
    // temperatureList 처리

    // 유효한 디바이스만 필터링
    const validDevices = localDevices.filter((item) => item && item.location);

    const temperatureList = validDevices.map((item) => {
      if (!item?.location?.identifier) {
        console.log("item", item);
      }
      return {
        id: item.location?.identifier,
        internalDisplayName: item.internalDisplayName,
        location: item.location,
        labelType: item.labelType,
        timestamp: item.result?.temperature?.timestamp || "",
        tempValue: (item.result?.temperature && (item.result?.temperature?.value ?? item.result.temperature?.current)) ?? null,
      };
    });

    // 유효한 온도 값만 필터링 (null이나 NaN이 아닌 값들)
    const validTemperatures = temperatureList.filter((item) => typeof item.tempValue === "number" && !isNaN(item.tempValue));

    // 평균 온도 계산
    const sum = validTemperatures.reduce((acc, current) => acc + (current.tempValue as number), 0);
    const averageTemp = validTemperatures.length > 0 ? sum / validTemperatures.length : null;
    const formattedAverage = averageTemp !== null ? roundFloat(averageTemp) : "";

    return {
      temperatureList,
      formattedAverage,
    };
  }, [localDevices]); // localDevices가 변경될 때만 재계산

  return (
    <div ref={target}>
      <BaseRichTooltip
        rightLabel={<p className="font14">{formattedAverage !== "" && <span>{`${formattedAverage}°C`}</span>}</p>}
        children={
          <div>
            <p className="popover-gray__title">평균 온도</p>
            <p>
              {BULLET} {formattedAverage ? `${formattedAverage}°C` : "알 수 없음"}
            </p>

            {temperatureList.map((item, idx) => {
              // 유효하지 않은 항목은 건너뛰기
              if (!item || !item.location) return null;

              const tempDisplay = typeof item.tempValue === "number" && !isNaN(item.tempValue) ? `${roundFloat(item.tempValue)}°C` : "알 수 없음";

              return (
                <div key={`${item.location?.category || ""}-${item.labelType || ""}-${idx}`} className="flex-center">
                  <p>{`${BULLET} ${tempDisplay} ${item.internalDisplayName || ""}`}</p>
                  {isCaution(item.timestamp) && <div className="ml3">⚠️</div>}
                </div>
              );
            })}
          </div>
        }
      />
    </div>
  );
};

export default IotTemperatureAverage;
